import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { useToast } from "@/hooks/use-toast";
import { User, LogIn, Shield, AlertTriangle, CheckCircle2 } from "lucide-react";
import zxcvbn from "zxcvbn";

interface PostInquiryAuthDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  email: string;
  name?: string;
  phone?: string;
}

export function PostInquiryAuthDialog({
  open,
  onOpenChange,
  email,
  name,
  phone,
}: PostInquiryAuthDialogProps) {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [checkingUser, setCheckingUser] = useState(true);
  const [userExists, setUserExists] = useState(false);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [mode, setMode] = useState<"checking" | "signup" | "signin">("checking");

  // Password strength
  const strength = password ? zxcvbn(password) : null;
  const strengthScore = strength?.score ?? 0;
  const strengthLabel = !password
    ? ""
    : strengthScore <= 1
    ? "Very Weak"
    : strengthScore === 2
    ? "Weak"
    : strengthScore === 3
    ? "Fair"
    : "Strong";

  const isStrongEnough = password.length >= 8 && strengthScore >= 3;
  const passwordsMatch = password && confirmPassword && password === confirmPassword;

  // Check if user already exists when dialog opens
  useEffect(() => {
    if (open && email) {
      checkUserExists();
    }
  }, [open, email]);

  const checkUserExists = async () => {
    setCheckingUser(true);
    setMode("checking");

    try {
      // Try to sign in with a dummy password to check if user exists
      // If we get "Invalid login credentials", user exists
      // If we get "User not found" or similar, user doesn't exist
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password: "dummy_check_password_" + Date.now(),
      });

      if (error) {
        // "Invalid login credentials" means user exists but wrong password
        if (error.message.includes("Invalid login credentials")) {
          setUserExists(true);
          setMode("signin");
        } else {
          // User doesn't exist
          setUserExists(false);
          setMode("signup");
        }
      } else {
        // Shouldn't happen with dummy password, but handle it
        setUserExists(true);
        setMode("signin");
      }
    } catch (err) {
      console.error("Error checking user:", err);
      // Default to signup mode
      setUserExists(false);
      setMode("signup");
    } finally {
      setCheckingUser(false);
    }
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!passwordsMatch) {
      toast({
        title: "Passwords don't match",
        description: "Please make sure both passwords are the same.",
        variant: "destructive",
      });
      return;
    }

    if (!isStrongEnough) {
      toast({
        title: "Weak password",
        description: "Use at least 8 characters with a stronger mix.",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    try {
      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/`,
          data: {
            name: name || "",
          },
        },
      });

      if (error) throw error;

      // Update profile with phone if provided
      if (phone) {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          await supabase
            .from("profiles")
            .update({ phone, name })
            .eq("id", user.id);
        }
      }

      toast({
        title: "Account created!",
        description: "Welcome! You can now track your inquiries.",
      });

      onOpenChange(false);
      navigate("/dashboard", { replace: true });
    } catch (err: any) {
      toast({
        title: "Sign up failed",
        description: err?.message || "Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!password) {
      toast({
        title: "Password required",
        description: "Please enter your password.",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) throw error;

      toast({
        title: "Signed in!",
        description: "Welcome back!",
      });

      onOpenChange(false);
      navigate("/dashboard", { replace: true });
    } catch (err: any) {
      toast({
        title: "Sign in failed",
        description: err?.message || "Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSkip = () => {
    onOpenChange(false);
    navigate("/", { replace: true });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        {mode === "checking" ? (
          <div className="flex flex-col items-center justify-center py-8 space-y-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            <p className="text-muted-foreground">Checking your account...</p>
          </div>
        ) : mode === "signup" ? (
          <>
            <DialogHeader>
              <div className="flex items-center gap-2">
                <div className="p-2 rounded-full bg-primary/10">
                  <User className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <DialogTitle>Create Your Account</DialogTitle>
                  <DialogDescription>
                    Save your inquiry and track your events
                  </DialogDescription>
                </div>
              </div>
            </DialogHeader>

            <form onSubmit={handleSignUp} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Email</Label>
                <Input value={email} disabled className="bg-muted" />
              </div>

              <div className="space-y-2">
                <Label htmlFor="signup-password">Create Password</Label>
                <Input
                  id="signup-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="At least 8 characters"
                  minLength={8}
                  required
                  autoComplete="new-password"
                />
                {password.length > 0 && (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-muted-foreground">Strength</span>
                      <span className={strengthScore < 3 ? "text-destructive" : "text-primary"}>
                        {strengthLabel}
                      </span>
                    </div>
                    <Progress value={strengthScore * 25} className="h-1.5" />
                    {strength?.feedback?.warning && strengthScore < 3 && (
                      <div className="flex items-start gap-2 rounded-md border bg-muted/50 p-2">
                        <AlertTriangle className="h-3.5 w-3.5 text-muted-foreground mt-0.5" />
                        <p className="text-xs text-muted-foreground">{strength.feedback.warning}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="signup-confirm">Confirm Password</Label>
                <Input
                  id="signup-confirm"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm your password"
                  minLength={8}
                  required
                  autoComplete="new-password"
                />
                {confirmPassword.length > 0 && !passwordsMatch && (
                  <p className="text-xs text-destructive">Passwords don't match</p>
                )}
                {passwordsMatch && (
                  <p className="text-xs text-primary flex items-center gap-1">
                    <CheckCircle2 className="h-3 w-3" /> Passwords match
                  </p>
                )}
              </div>

              <div className="flex gap-2 pt-2">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={handleSkip}
                  className="flex-1"
                >
                  Skip for now
                </Button>
                <Button
                  type="submit"
                  className="flex-1"
                  disabled={isLoading || !passwordsMatch || !isStrongEnough}
                >
                  {isLoading ? "Creating..." : "Create Account"}
                </Button>
              </div>

              <p className="text-xs text-center text-muted-foreground">
                Already have an account?{" "}
                <button
                  type="button"
                  onClick={() => {
                    setMode("signin");
                    setPassword("");
                    setConfirmPassword("");
                  }}
                  className="text-primary hover:underline"
                >
                  Sign in instead
                </button>
              </p>
            </form>
          </>
        ) : (
          <>
            <DialogHeader>
              <div className="flex items-center gap-2">
                <div className="p-2 rounded-full bg-primary/10">
                  <LogIn className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <DialogTitle>Welcome Back!</DialogTitle>
                  <DialogDescription>
                    Sign in to link this inquiry to your account
                  </DialogDescription>
                </div>
              </div>
            </DialogHeader>

            <form onSubmit={handleSignIn} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Email</Label>
                <Input value={email} disabled className="bg-muted" />
              </div>

              <div className="space-y-2">
                <Label htmlFor="signin-password">Password</Label>
                <Input
                  id="signin-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  required
                  autoComplete="current-password"
                />
              </div>

              <div className="flex gap-2 pt-2">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={handleSkip}
                  className="flex-1"
                >
                  Skip for now
                </Button>
                <Button type="submit" className="flex-1" disabled={isLoading || !password}>
                  {isLoading ? "Signing in..." : "Sign In"}
                </Button>
              </div>

              <p className="text-xs text-center text-muted-foreground">
                Don't have an account?{" "}
                <button
                  type="button"
                  onClick={() => {
                    setMode("signup");
                    setPassword("");
                    setConfirmPassword("");
                  }}
                  className="text-primary hover:underline"
                >
                  Create one instead
                </button>
              </p>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
